import { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, doc, updateDoc, where, Timestamp, deleteField } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../hooks';
import { Button } from '../common/Button';
import toast from 'react-hot-toast';

interface CorrectionRequest {
  id: string;
  type: 'reference' | 'calibre' | 'brand';
  brandId?: string;
  brandName?: string;
  referenceId?: string;
  reference?: string;
  calibreName?: string;
  field: string;
  description: string;
  sourceUrl?: string;
  submittedBy: string;
  submittedByEmail?: string;
  submittedAt: Date;
  status: 'pending' | 'reviewed' | 'applied' | 'rejected';
  reviewNotes?: string;
  reviewedBy?: string;
  reviewedByEmail?: string;
}

type StatusFilter = 'all' | 'pending' | 'reviewed' | 'applied' | 'rejected';

export function CorrectionRequestsManager() {
  const { user } = useAuth();
  const [requests, setRequests] = useState<CorrectionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [selectedRequest, setSelectedRequest] = useState<CorrectionRequest | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadRequests();
  }, [statusFilter]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const col = collection(db, 'correction_requests');
      const q = statusFilter === 'all'
        ? query(col, orderBy('submittedAt', 'desc'))
        : query(col, where('status', '==', statusFilter), orderBy('submittedAt', 'desc'));
      const snapshot = await getDocs(q);
      const items: CorrectionRequest[] = snapshot.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          submittedAt: data.submittedAt?.toDate?.() || new Date(),
        } as CorrectionRequest;
      });
      setRequests(items);
    } catch (err) {
      console.error('Failed to load correction requests:', err);
      toast.error('Failed to load correction requests');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, status: CorrectionRequest['status']) => {
    setUpdating(true);
    try {
      const trimmedNotes = reviewNotes.trim();
      // Firestore is initialised WITHOUT `ignoreUndefinedProperties`, so any
      // `undefined` in the payload makes updateDoc() throw before it hits the
      // network. Empty notes therefore use deleteField() (a no-op when the
      // field was never set, and the only way to actually clear notes the
      // reviewer wiped out of the prefilled textarea), and the loop below is a
      // belt-and-braces guard for the optional reviewer fields.
      const payload: Record<string, unknown> = {
        status,
        reviewNotes: trimmedNotes ? trimmedNotes : deleteField(),
        reviewedAt: Timestamp.now(),
        reviewedBy: user?.uid,
        reviewedByEmail: user?.email ?? null,
      };
      for (const key of Object.keys(payload)) {
        if (payload[key] === undefined) delete payload[key];
      }
      await updateDoc(doc(db, 'correction_requests', id), payload);
      toast.success(`Request marked as ${status}`);
      setSelectedRequest(null);
      setReviewNotes('');
      loadRequests();
    } catch (err) {
      console.error('Failed to update correction request:', id, status, err);
      toast.error(
        `Failed to update request: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setUpdating(false);
    }
  };

  const getTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      reference: 'bg-blue-100 text-blue-700',
      calibre: 'bg-purple-100 text-purple-700',
      brand: 'bg-green-100 text-green-700',
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-700',
      reviewed: 'bg-blue-100 text-blue-700',
      applied: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getItemLabel = (req: CorrectionRequest) => {
    if (req.type === 'reference') return `${req.brandName || ''} ${req.reference || ''}`.trim();
    if (req.type === 'calibre') return req.calibreName || '';
    if (req.type === 'brand') return req.brandName || '';
    return '';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Correction Requests</h2>
        <div className="flex gap-2">
          {(['pending', 'reviewed', 'applied', 'rejected', 'all'] as StatusFilter[]).map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full capitalize transition-colors ${
                statusFilter === s
                  ? 'bg-brand-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No {statusFilter === 'all' ? '' : statusFilter} correction requests found.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Field</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted By</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {requests.map(req => (
                <tr
                  key={req.id}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => { setSelectedRequest(req); setReviewNotes(req.reviewNotes || ''); }}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getTypeBadge(req.type)}`}>
                      {req.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 max-w-[200px] truncate">
                    {getItemLabel(req)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{req.field}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{req.submittedByEmail || req.submittedBy.slice(0, 8)}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                    {req.submittedAt.toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadge(req.status)}`}>
                      {req.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail panel (modal/slide-over) */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelectedRequest(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getTypeBadge(selectedRequest.type)}`}>
                    {selectedRequest.type}
                  </span>
                  <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadge(selectedRequest.status)}`}>
                    {selectedRequest.status}
                  </span>
                </div>
                <button onClick={() => setSelectedRequest(null)} className="text-gray-400 hover:text-gray-600">
                  ✕
                </button>
              </div>

              {/* Item info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{getItemLabel(selectedRequest)}</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Field: <span className="font-medium text-gray-700">{selectedRequest.field}</span>
                </p>
              </div>

              {/* Description */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Correction Description</label>
                <p className="mt-1 text-sm text-gray-900 bg-gray-50 rounded-lg p-3">
                  {selectedRequest.description}
                </p>
              </div>

              {/* Source URL */}
              {selectedRequest.sourceUrl && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Source</label>
                  <a
                    href={selectedRequest.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 block text-sm text-brand-600 hover:text-brand-700 underline break-all"
                  >
                    {selectedRequest.sourceUrl}
                  </a>
                </div>
              )}

              {/* Metadata */}
              <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                <span>By: {selectedRequest.submittedByEmail || selectedRequest.submittedBy}</span>
                <span>On: {selectedRequest.submittedAt.toLocaleDateString()}</span>
                {(selectedRequest.reviewedByEmail || selectedRequest.reviewedBy) && (
                  <span>
                    Reviewed by: {selectedRequest.reviewedByEmail || selectedRequest.reviewedBy}
                  </span>
                )}
              </div>

              {/* Review notes */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Review Notes (optional)</label>
                <textarea
                  className="mt-1 w-full border border-gray-200 rounded-lg p-2 text-sm"
                  rows={2}
                  placeholder="Add notes about this correction..."
                  value={reviewNotes}
                  onChange={e => setReviewNotes(e.target.value)}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => updateStatus(selectedRequest.id, 'applied')}
                  disabled={updating}
                >
                  Mark Applied
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => updateStatus(selectedRequest.id, 'reviewed')}
                  disabled={updating}
                >
                  Mark Reviewed
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => updateStatus(selectedRequest.id, 'rejected')}
                  disabled={updating}
                >
                  Reject
                </Button>
              </div>

              {/* Quick navigation */}
              {selectedRequest.status === 'pending' && (
                <div className="border-t pt-3 mt-3">
                  <p className="text-xs text-gray-500 mb-2">Quick Navigation</p>
                  <p className="text-xs text-gray-400">
                    Navigate to the <span className="font-medium">Curated {selectedRequest.type === 'calibre' ? 'Calibres' : 'Watches'}</span> tab
                    to find and edit {getItemLabel(selectedRequest)}.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
